import { useState } from 'react';

import { GasPipelineSectionStatus } from '@gasbi/shared/common/features/gasPipelineSections/types';
import { Breadcrumbs } from '@gasbi/shared/web/components/Breadcrumbs';
import { PageTitle } from '@gasbi/shared/web/components/PageTitle';
import { useWatchPageLoading } from '@gasbi/shared/web/providers/PageProvider';
import { useNavigate, useParams } from 'react-router-dom';

import { DocumentsList } from '@/components/ActsAndDocumentsForm';
import { useFetchBalanceAct } from '@/components/ActsAndDocumentsForm/hooks/useFetchBalanceAct';
import {
  useGetCachedBalanceActByIdSelector,
  useGetLossHistoryQuery,
} from '@/features/balanceActs';
import { useGetCommercialMeteringPointByIdQuery } from '@/features/commercialMeteringPoint/commercialMeteringPointsApi';
import { useBreadcrumbsNavConfig } from '@/pages/MeteringPointActDetails/hooks/useBreadcrumbsNavConfig';
import { getUseQueryHookState } from '@/utils';

import { CircularProgress, Stack } from '@mui/joy';
import Button from '@mui/joy/Button';

import { ActOwner } from '@components/ActsAndDocumentsForm';
import { DateChip } from '@components/ActsAndDocumentsForm';
import { PageDetailsContainer } from '@components/BaseStyledComponents';
import { IsModificationAllowedByEntity } from '@components/IsModificationAllowed';
import NavLinkTabs from '@components/NavLinkTabs';
import { EmptyMessage } from '@components/shared/grid';
import { InfoBoxRow } from '@components/ui/InfoBox';

import BridgeActs from '@assets/BridgeActs.svg?react';

import { GeneralInformation } from './components/GeneralInformation';
import { PipelineSectionsTable } from './components/PipelineSectionsTable';

const CommercialMeteringPointBalanceActDetails = () => {
  const navigate = useNavigate();
  const { navConfig } = useBreadcrumbsNavConfig();

  const [tabIdx, setTabIdx] = useState<number>(0);

  const { commercialMeteringPointId, balanceActId } = useParams<{
    commercialMeteringPointId?: string;
    balanceActId: string;
  }>();

  const { balanceAct, ...balanceActQuery } = useFetchBalanceAct();

  const cachedBalanceAct = useGetCachedBalanceActByIdSelector(balanceActId);

  const {
    data: { records: lossHistory, technologicalLossErrorMessage } = {},
    ...lossHistoryQuery
  } = useGetLossHistoryQuery(balanceActId);

  const { data: commercialMeteringPoint, ...commercialMeteringPointByIdQuery } =
    useGetCommercialMeteringPointByIdQuery(commercialMeteringPointId);

  const isLoading = getUseQueryHookState('isLoading', [
    commercialMeteringPointByIdQuery,
    balanceActQuery,
    lossHistoryQuery,
  ]);

  const isError = getUseQueryHookState('isError', [
    commercialMeteringPointByIdQuery,
    balanceActQuery,
    lossHistoryQuery,
  ]);

  const maxGasConsumption = lossHistory?.[0]?.maxDailyTechnologicalLoss ?? 0;

  useWatchPageLoading(isLoading);

  const allowToEdit = balanceAct?.gasPipelineSections.every(
    section => section?.status === GasPipelineSectionStatus.Undefined,
  );

  if (isError) {
    return <div>Помилка завантаження</div>;
  }

  if (isLoading || !balanceAct) {
    return (
      <Stack alignItems='center' justifyContent='center' height='100vh'>
        <CircularProgress />
      </Stack>
    );
  }

  const title = `Акт балансової належності №${balanceAct.number || cachedBalanceAct?.number}`;

  return (
    <PageDetailsContainer sx={{ gap: 2 }}>
      <Breadcrumbs nav={navConfig} />

      <PageTitle
        title={title}
        status={<DateChip startDate={balanceAct.date} />}
        icon={BridgeActs}
        onArrowBack={() => navigate('../..', { relative: 'path' })}
      />

      <NavLinkTabs
        links={[
          {
            title: 'Загальна інформація',
            className: tabIdx === 0 && 'active',
            component: null,
            path: '',
            onClick: () => setTabIdx(0),
          },
          {
            title: 'Додані документи',
            className: tabIdx === 1 && 'active',
            component: null,
            path: '',
            onClick: () => setTabIdx(1),
          },
        ]}
      >
        {allowToEdit && (
          <IsModificationAllowedByEntity entity={balanceAct}>
            <Button
              size='sm'
              variant='solid'
              onClick={() => navigate(`form`, { relative: 'path' })}
            >
              Редагувати
            </Button>
          </IsModificationAllowedByEntity>
        )}
      </NavLinkTabs>

      {tabIdx === 0 && (
        <>
          <InfoBoxRow alignItems='stretch'>
            <GeneralInformation
              maxGasConsumption={maxGasConsumption}
              isLoading={isLoading}
              lossHistory={lossHistory}
              technologicalLossErrorMessage={technologicalLossErrorMessage}
            />
            <ActOwner
              isLoading={isLoading}
              meteringPoint={commercialMeteringPoint}
              currentActDate={balanceAct.date}
            />
          </InfoBoxRow>

          <PipelineSectionsTable
            sections={balanceAct.gasPipelineSections}
            isLoading={isLoading}
          />
        </>
      )}

      {tabIdx === 1 && (
        <>
          {balanceAct.documents.length ? (
            <Stack>
              <DocumentsList documents={balanceAct.documents} />
            </Stack>
          ) : (
            <EmptyMessage title='Прикріплені документи відсутні' />
          )}
        </>
      )}
    </PageDetailsContainer>
  );
};

export default CommercialMeteringPointBalanceActDetails;
